"use client";

import { CheckIcon, Loader2Icon } from "lucide-react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { LayoutGroup, motion, useReducedMotion } from "motion/react";
import { type ReactNode, useEffect, useState } from "react";
import { cn } from "../../lib";
import { SQ_GREEN_LINEAR_GRADIENT_CLASS } from "../../lib/sq-color-runtime-exports";
import {
	isPromiseLike,
	LIVE_SAVING_SUCCESS_TICK_DURATION_MS,
	useLiveSavingPhase,
} from "./use-live-saving-phase";

export type ToggleSwitchOption<T extends string> = {
	id: T;
	ariaLabel: string;
	/** Optional bottom tooltip (shadcn). */
	tooltip?: ReactNode;
} & ({ icon: ReactNode; label?: never } | { label: string; icon?: never });

export interface ToggleSwitchProps<T extends string> {
	value: T;
	onValueChange: (value: T) => void | Promise<void>;
	options: readonly ToggleSwitchOption<T>[];
	/** Unique per control instance (enables shared layout animation within a group). */
	layoutId: string;
	/** Icon buttons (default) or compact uppercase text labels. */
	optionDisplay?: "icon" | "text";
	/**
	 * `default` — option hit targets `size-8` / `h-8` (track ~36px with padding).
	 * `compact` — total track height matches CircleButton `sm` (`size-8` / 32px).
	 */
	size?: "default" | "compact";
	/**
	 * When false, the active pill is a static absolute fill (no `layoutId` projection).
	 * Use while a parent is dragging/resizing so the pill stays coupled to the track;
	 * re-enable for smooth option-to-option transitions.
	 */
	layoutAnimationsEnabled?: boolean;
	className?: string;
	disabled?: boolean;
	/** Neutral starting state: pill centered between options; only explicit picks commit. */
	mixed?: boolean;
	/** When true, track uses learn completed hex path tile greens while the right-most option is selected. */
	highlightWhenRightActive?: boolean;
	"aria-invalid"?: boolean;
	/** How long the success tick shows after an async `onValueChange` resolves. */
	successTickDurationMs?: number;
}

const TOGGLE_SWITCH_ACTIVE_BG_SPRING = {
	type: "spring" as const,
	stiffness: 420,
	damping: 28,
	mass: 0.82,
};

const TRACK_TRANSITION_CLASS =
	"transition-[background-color,box-shadow,ring-color] duration-300 ease-in-out";

const TRACK_SHELL_CLASS = cn(
	"relative inline-flex items-center gap-0.5 rounded-full p-0.5 ring-[0.5px] ring-inset",
	TRACK_TRANSITION_CLASS,
);

/** Compact track: 2px pad + 28px options = 32px — matches CircleButton `sm`. */
const TRACK_SHELL_COMPACT_CLASS = cn(
	"relative inline-flex items-center gap-px rounded-full p-0.5 ring-[0.5px] ring-inset",
	TRACK_TRANSITION_CLASS,
);

/**
 * Recessed track shell — aligned with tailwind `Switch` track (`bg-zinc-200` /
 * `dark:bg-white/5`) with inset depth.
 */
const TRACK_NEUTRAL_SHELL_CLASS = cn(
	"bg-zinc-200 ring-black/[0.06]",
	"shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.5)]",
	"dark:bg-white/5 dark:ring-white/10",
	"dark:shadow-[inset_0_2px_6px_0_rgba(0,0,0,0.36),inset_0_1px_0_rgba(255,255,255,0.07)]",
);

/** Track shell while accent fill is visible (autoplay on) — lime-olive ring, not neutral zinc. */
const TRACK_ACCENT_SHELL_CLASS = cn(
	"bg-transparent ring-[#687801]/45",
	"shadow-[inset_0_2px_5px_0_rgba(42,54,29,0.2),inset_0_1px_0_rgba(255,255,255,0.16)]",
	"dark:ring-[color-mix(in_srgb,var(--sq-green-static-from)_38%,transparent)]",
	"dark:shadow-[inset_0_2px_6px_0_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)]",
);

/** Completed hex path tile greens (sq green static pair). */
const TRACK_ACCENT_GRADIENT_CLASS = SQ_GREEN_LINEAR_GRADIENT_CLASS;

function resolveTrackShellClassName(size: "default" | "compact"): string {
	return size === "compact" ? TRACK_SHELL_COMPACT_CLASS : TRACK_SHELL_CLASS;
}

function resolveTrackClassName(
	showAccentTrack: boolean,
	size: "default" | "compact" = "default",
): string {
	return cn(
		resolveTrackShellClassName(size),
		showAccentTrack ? TRACK_ACCENT_SHELL_CLASS : TRACK_NEUTRAL_SHELL_CLASS,
	);
}

const OPTION_BUTTON_BASE_CLASS =
	"relative z-[1] flex cursor-pointer items-center justify-center rounded-full transition-[color,opacity] duration-300 ease-in-out disabled:cursor-not-allowed disabled:opacity-40";

const OPTION_BUTTON_ICON_CLASS = cn(OPTION_BUTTON_BASE_CLASS, "size-8");
const OPTION_BUTTON_ICON_COMPACT_CLASS = cn(OPTION_BUTTON_BASE_CLASS, "size-7");

const OPTION_BUTTON_TEXT_CLASS = cn(
	OPTION_BUTTON_BASE_CLASS,
	"h-8 px-2.5 text-[10px] font-black uppercase tracking-[0.12em]",
);
const OPTION_BUTTON_TEXT_COMPACT_CLASS = cn(
	OPTION_BUTTON_BASE_CLASS,
	"h-7 px-2 text-[9px] font-black uppercase tracking-[0.1em]",
);

function resolveOptionButtonClassName(
	optionDisplay: "icon" | "text",
	size: "default" | "compact",
): string {
	if (optionDisplay === "text") {
		return size === "compact"
			? OPTION_BUTTON_TEXT_COMPACT_CLASS
			: OPTION_BUTTON_TEXT_CLASS;
	}
	return size === "compact"
		? OPTION_BUTTON_ICON_COMPACT_CLASS
		: OPTION_BUTTON_ICON_CLASS;
}

function renderToggleSwitchOptionContent<T extends string>(
	option: ToggleSwitchOption<T>,
	optionDisplay: "icon" | "text",
): ReactNode {
	if (optionDisplay === "text") {
		return option.label;
	}
	return option.icon;
}

const INACTIVE_ON_NEUTRAL_TRACK_CLASS =
	"opacity-50 text-neutral-600 hover:text-neutral-900 hover:opacity-100 dark:text-neutral-400 dark:hover:text-neutral-100";

const INACTIVE_ON_ACCENT_TRACK_CLASS =
	"opacity-50 text-white hover:text-white hover:opacity-100 dark:text-white dark:hover:text-white";

const ACTIVE_OPTION_CLASS =
	"opacity-100 text-neutral-900 dark:text-neutral-900";

const ACTIVE_PILL_BASE_CLASS = cn(
	"pointer-events-none absolute inset-0 z-[1] ring-[0.5px] ring-inset",
	"transition-[background-color,box-shadow,ring-color,width,border-radius] duration-300 ease-in-out",
);

/** Centered neutral pill when scope values are mixed — narrow bar so on/off labels stay visible. */
const MIXED_PILL_LAYOUT_CLASS = cn(
	"inset-y-0.5 left-1/2 right-auto w-[calc(25%-2px)] -translate-x-1/2 rounded-md",
);

/** Sliding pill on zinc / muted track (format toggle, autoplay off, etc.). */
const ACTIVE_PILL_ON_NEUTRAL_TRACK_CLASS = cn(
	"bg-white ring-black/[0.07]",
	"shadow-[inset_0_1px_0_0_rgba(255,255,255,0.92),inset_0_-1px_1px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.07)]",
	"dark:bg-white dark:ring-white/12",
	"dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.38),inset_0_-1px_1px_0_rgba(0,0,0,0.22),0_1px_2.5px_0_rgba(0,0,0,0.38)]",
);

/** Sliding pill on completed-path accent track (autoplay on). */
const ACTIVE_PILL_ON_ACCENT_TRACK_CLASS = cn(
	"bg-[#FCFDF7] ring-[#687801]/40",
	"shadow-[inset_0_1px_0_0_rgba(255,255,255,0.96),inset_0_-1px_1px_0_rgba(42,54,29,0.06),0_1px_2.5px_0_rgba(42,54,29,0.14)]",
	"dark:bg-[#F6F8EF] dark:ring-[color-mix(in_srgb,var(--sq-green-static-from)_42%,transparent)]",
	"dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.42),inset_0_-1px_1px_0_rgba(0,0,0,0.18),0_1px_3px_0_rgba(0,0,0,0.34)]",
);

function resolveActivePillClassName(showAccentTrack: boolean): string {
	return cn(
		ACTIVE_PILL_BASE_CLASS,
		"rounded-full",
		showAccentTrack
			? ACTIVE_PILL_ON_ACCENT_TRACK_CLASS
			: ACTIVE_PILL_ON_NEUTRAL_TRACK_CLASS,
	);
}

function resolveInactiveOptionClassName(
	highlightWhenRightActive: boolean,
	isRightActive: boolean,
): string {
	return highlightWhenRightActive && isRightActive
		? INACTIVE_ON_ACCENT_TRACK_CLASS
		: INACTIVE_ON_NEUTRAL_TRACK_CLASS;
}

function LiveSavingOptionOverlay({
	phase,
}: {
	phase: "processing" | "success";
}) {
	if (phase === "processing") {
		return (
			<span className="absolute inset-0 z-[3] flex items-center justify-center rounded-full bg-background/80">
				<Loader2Icon className="size-3.5 animate-spin" aria-hidden />
			</span>
		);
	}
	return (
		<span className="absolute inset-0 z-[3] flex items-center justify-center rounded-full">
			<CheckIcon
				className={cn("size-3.5", ACTIVE_OPTION_CLASS)}
				aria-hidden
			/>
		</span>
	);
}

export function ToggleSwitch<T extends string>({
	value,
	onValueChange,
	options,
	layoutId,
	optionDisplay = "icon",
	size = "default",
	layoutAnimationsEnabled = true,
	className,
	disabled = false,
	mixed = false,
	highlightWhenRightActive = false,
	"aria-invalid": ariaInvalid,
	successTickDurationMs = LIVE_SAVING_SUCCESS_TICK_DURATION_MS,
}: ToggleSwitchProps<T>) {
	const [liveSavingOptionId, setLiveSavingOptionId] = useState<T | null>(
		null,
	);
	const { phase, isBusy, runLiveSaving } = useLiveSavingPhase({
		successTickDurationMs,
	});
	const optionButtonClassName = resolveOptionButtonClassName(
		optionDisplay,
		size,
	);

	useEffect(() => {
		if (phase === "idle") {
			setLiveSavingOptionId(null);
		}
	}, [phase]);
	const prefersReducedMotion = useReducedMotion();
	const activeBgMotionTransition =
		prefersReducedMotion === true
			? ({ duration: 0.18, ease: "easeOut" } as const)
			: ({ layout: TOGGLE_SWITCH_ACTIVE_BG_SPRING } as const);

	const rightOptionId = options[options.length - 1]?.id;
	const isRightActive =
		!mixed && rightOptionId !== undefined && value === rightOptionId;
	const showAccentTrack = highlightWhenRightActive && isRightActive;
	const inactiveOptionClassName = resolveInactiveOptionClassName(
		highlightWhenRightActive,
		isRightActive,
	);
	const activePillClassName = resolveActivePillClassName(showAccentTrack);
	const trackClassName = resolveTrackClassName(showAccentTrack, size);
	const isControlDisabled = disabled || isBusy;

	const handleOptionClick = (optionId: T) => {
		if (isControlDisabled || (!mixed && optionId === value)) {
			return;
		}
		const result = onValueChange(optionId);
		if (!isPromiseLike(result)) {
			return;
		}
		setLiveSavingOptionId(optionId);
		void runLiveSaving(async () => {
			await result;
		});
	};

	function renderActivePill(extraClassName?: string) {
		const pillClassName = cn(activePillClassName, extraClassName);
		if (!layoutAnimationsEnabled) {
			return <span className={pillClassName} aria-hidden />;
		}
		return (
			<motion.span
				layoutId={`${layoutId}-active-pill`}
				layoutDependency={value}
				className={pillClassName}
				transition={activeBgMotionTransition}
			/>
		);
	}

	const hasOptionTooltips = options.some((option) => option.tooltip != null);

	const track = (
		<LayoutGroup id={`${layoutId}-group`}>
			{/*
			  layoutRoot: option-to-option layoutId stays relative to the track, so a
			  parent drag/resize (e.g. learn topdrawer) does not project the pill in
			  viewport space independently of the bar.
			*/}
			<motion.div
				layoutRoot
				className={cn(trackClassName, className)}
				role="group"
				aria-busy={isBusy || undefined}
				aria-disabled={isControlDisabled || undefined}
				aria-invalid={ariaInvalid ? true : undefined}
			>
				{highlightWhenRightActive ? (
					<span
						aria-hidden
						className={cn(
							"pointer-events-none absolute inset-0 rounded-full transition-opacity duration-300 ease-in-out",
							TRACK_ACCENT_GRADIENT_CLASS,
							showAccentTrack ? "opacity-100" : "opacity-0",
						)}
					/>
				) : null}
				{mixed ? (
					layoutAnimationsEnabled ? (
						<motion.span
							key={`${layoutId}-mixed-pill`}
							layoutId={`${layoutId}-active-pill`}
							layoutDependency={value}
							className={cn(
								activePillClassName,
								MIXED_PILL_LAYOUT_CLASS,
							)}
							transition={activeBgMotionTransition}
							initial={{ opacity: 1 }}
							animate={{ opacity: 1 }}
						/>
					) : (
						<span
							className={cn(
								activePillClassName,
								MIXED_PILL_LAYOUT_CLASS,
							)}
							aria-hidden
						/>
					)
				) : null}
				{options.map((option) => {
					const isActive = !mixed && value === option.id;
					const showLiveSavingOverlay =
						liveSavingOptionId === option.id && phase !== "idle";
					const optionButton = (
						<button
							type="button"
							className={cn(
								optionButtonClassName,
								showLiveSavingOverlay && "z-[2]",
								isActive
									? ACTIVE_OPTION_CLASS
									: inactiveOptionClassName,
							)}
							aria-pressed={isActive}
							aria-label={option.ariaLabel}
							disabled={isControlDisabled}
							onClick={() => handleOptionClick(option.id)}
						>
							{showLiveSavingOverlay ? (
								<LiveSavingOptionOverlay
									phase={
										phase === "processing"
											? "processing"
											: "success"
									}
								/>
							) : null}
							<span
								className={cn(
									"transition-opacity duration-150",
									showLiveSavingOverlay && "opacity-0",
								)}
								aria-hidden={
									showLiveSavingOverlay || undefined
								}
							>
								{renderToggleSwitchOptionContent(
									option,
									optionDisplay,
								)}
							</span>
						</button>
					);
					return (
						<span
							key={option.id}
							className="relative z-0 inline-flex items-center"
						>
							{/* One shared layout pill — no AnimatePresence exit ghosts. */}
							{isActive ? renderActivePill() : null}
							{option.tooltip != null ? (
																<ContentTooltip
									{...CONTENT_TOOLTIP_SHORT}
									side="bottom"
									content={option.tooltip}
								>
									{optionButton}
								</ContentTooltip>
							) : (
								optionButton
							)}
						</span>
					);
				})}
			</motion.div>
		</LayoutGroup>
	);

	if (!hasOptionTooltips) {
		return track;
	}

	return track;
}
